import React from 'react';
import { Sparkles, Eye, Shield } from 'lucide-react';
import WhatWeSeePanel from './privacy/WhatWeSeePanel';
import SecurityReviewStatus from './privacy/SecurityReviewStatus';
import PrivacyModeCard from './settings/PrivacyModeCard';
import CodeNameCard from './settings/CodeNameCard';
import PrivacyPresetsCard from './settings/PrivacyPresetsCard';
import AwayCheckinCard from './settings/AwayCheckinCard';
import CookiePreferencesPanel from './CookiePreferencesPanel';

/**
 * PrivacyTab — the single "Your Privacy" home. All privacy controls live
 * here (moved out of Settings so members have one place to think about
 * privacy, not two).
 *
 * Layout:
 *   1. Warm heading + "replay welcome slides" nudge
 *   2. What We Can See vs. What Only You Can See — reassurance
 *   3. The three real working cards (Privacy Mode, Code Name, presets)
 *   4. "Two more that quietly protect you" — Hide Amounts + Auto-Lock info
 *   5. Independent Security Review status + Privacy Policy link
 */
const AlwaysOnItem = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-emerald-700" aria-hidden="true" />
    </div>
    <div>
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const PrivacyTab = ({ user, onReplayWelcome }) => (
  <div className="space-y-8" data-testid="privacy-tab-content">
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
      <div>
        <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Your Privacy</h2>
        <p className="body-text mt-1">Every control that helps you stay invisible on MyInvestFolio, in one place.</p>
      </div>
      {onReplayWelcome && (
        <button
          type="button"
          onClick={onReplayWelcome}
          data-testid="replay-privacy-welcome"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-emerald-700 hover:text-emerald-900 self-start sm:self-auto"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Replay welcome slides
        </button>
      )}
    </div>

    <WhatWeSeePanel />

    <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6" data-testid="privacy-controls-section">
      <div className="border-l-2 border-emerald-500 pl-3 mb-4">
        <h3 className="text-base sm:text-lg font-bold text-slate-900">Your Privacy Controls</h3>
        <p className="text-sm text-slate-500 mt-0.5">Change any of these any time. Nothing you set here is ever visible to us.</p>
      </div>
      <div className="space-y-6">
        <PrivacyModeCard />
        <CodeNameCard />
        <PrivacyPresetsCard />
        <AwayCheckinCard />
      </div>
    </div>

    <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-6">
      <div className="border-l-2 border-emerald-500 pl-3 mb-4">
        <h3 className="text-base sm:text-lg font-bold text-slate-900">Two more that quietly protect you</h3>
        <p className="text-sm text-slate-500 mt-0.5">Reached from the top of every page &mdash; no setting needed.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <AlwaysOnItem
          icon={Eye}
          title="Hide Amounts (the eye button)"
          subtitle="Tap the eye in the header. Every dollar and percentage turns into dashes. Tap again to bring them back."
        />
        <AlwaysOnItem
          icon={Shield}
          title="Auto-Lock"
          subtitle="Always on. After a short spell of no activity we fog the screen and ask for your password to come back."
        />
      </div>
    </div>

    <CookiePreferencesPanel />

    <SecurityReviewStatus />
  </div>
);

export default PrivacyTab;
